import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Input, Select, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  AdvancedFiltersPanelWrapper,
  AdvancedFiltersPanelHeader,
  AdvancedFiltersPanelTitle,
  AdvancedFiltersFieldsRow,
  AdvancedFilterField,
  AdvancedFiltersActions,
  ActionLabel,
  Dropdown,
  Tooltip
} from './styled';

const { Option } = Select;

const AdvancedFiltersPanel = ({
  filters,
  onApply,
  onClose,
  typologies = [],
  transactionTypes = [],
  isTriggeredOnly,
  isTMTransaction = true,
  hideAccountId = false,
  hideTypologies = false
}) => {
  const [draft, setDraft] = useState({
    accountId: filters.accountId || '',
    transactionType: filters.transactionType,
    typologyId: filters.typologyId || 'all'
  });

  useEffect(() => {
    setDraft({
      accountId: filters.accountId || '',
      transactionType: filters.transactionType,
      typologyId: filters.typologyId || 'all'
    });
  }, [filters.accountId, filters.transactionType, filters.typologyId]);

  const typologiesOptions = useMemo(() => {
    const list = Array.isArray(typologies) ? typologies : [];
    return [
      { id: 'all', name: isTriggeredOnly ? 'All' : '' },
      ...list.map(({ id, name }) => ({ id, name }))
    ];
  }, [typologies, isTriggeredOnly]);

  const transactionTypeOptions = useMemo(() => {
    const list = Array.isArray(transactionTypes) ? transactionTypes : [];
    return [
      { value: '', label: 'All' },
      ...list.map((t) => ({
        value: typeof t === 'string' ? t : t.code || t.value || t,
        label:
          typeof t === 'string' ? t : t.description || t.label || t.code || t
      }))
    ];
  }, [transactionTypes]);

  const isDefault =
    !draft.accountId?.trim() &&
    !draft.transactionType &&
    (!draft.typologyId || draft.typologyId === 'all');

  const hasChanges =
    (draft.accountId?.trim() || '') !== (filters.accountId || '') ||
    (draft.transactionType || undefined) !==
      (filters.transactionType || undefined) ||
    (draft.typologyId || 'all') !== (filters.typologyId || 'all');

  const isAppliedDefault =
    !filters.accountId &&
    !filters.transactionType &&
    (!filters.typologyId || filters.typologyId === 'all');

  const handleClear = () => {
    const cleared = {
      accountId: undefined,
      transactionType: undefined,
      typologyId: 'all'
    };
    setDraft({
      accountId: '',
      transactionType: undefined,
      typologyId: 'all'
    });
    if (!isAppliedDefault) {
      onApply(cleared);
      onClose();
    }
  };

  const handleApply = () => {
    onApply({
      accountId: draft.accountId?.trim() || undefined,
      transactionType: draft.transactionType || undefined,
      typologyId: draft.typologyId || 'all'
    });
  };

  return (
    <AdvancedFiltersPanelWrapper>
      <AdvancedFiltersPanelHeader>
        <AdvancedFiltersPanelTitle>Advanced Filters</AdvancedFiltersPanelTitle>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          size="small"
        />
      </AdvancedFiltersPanelHeader>

      <AdvancedFiltersFieldsRow>
        {!hideAccountId && (
          <AdvancedFilterField>
            <ActionLabel>Account Number</ActionLabel>
            <Input
              placeholder="Filter by account number"
              value={draft.accountId}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, accountId: e.target.value }))
              }
              allowClear
            />
          </AdvancedFilterField>
        )}

        <AdvancedFilterField>
          <ActionLabel>Transaction Type</ActionLabel>
          <Select
            value={draft.transactionType || ''}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                transactionType: value || undefined
              }))
            }
            style={{ width: '100%' }}>
            {transactionTypeOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </AdvancedFilterField>

        {isTMTransaction && !hideTypologies && (
          <AdvancedFilterField>
            <ActionLabel>Typologies</ActionLabel>
            <Dropdown
              disabled={!isTriggeredOnly}
              dropdownValue={typologiesOptions.find(
                ({ id }) => id === draft.typologyId
              )}
              defaultValue="all"
              dropdownOptions={typologiesOptions.map((option) => ({
                ...option,
                name: <Tooltip content={option.name}>{option.name}</Tooltip>
              }))}
              handleValueChange={(typology) =>
                setDraft((prev) => ({ ...prev, typologyId: typology.id }))
              }
            />
          </AdvancedFilterField>
        )}
      </AdvancedFiltersFieldsRow>

      <AdvancedFiltersActions>
        <Button onClick={handleClear} disabled={isDefault}>
          Clear Filters
        </Button>
        <Button type="primary" onClick={handleApply} disabled={!hasChanges}>
          Apply
        </Button>
      </AdvancedFiltersActions>
    </AdvancedFiltersPanelWrapper>
  );
};

AdvancedFiltersPanel.propTypes = {
  filters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  typologies: PropTypes.array,
  transactionTypes: PropTypes.array,
  isTriggeredOnly: PropTypes.bool,
  isTMTransaction: PropTypes.bool
};

export default AdvancedFiltersPanel;
